import { Add } from '@mui/icons-material';
import { Button, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useMutateCreateApplicationRegister } from '/@/hooks/application-register';
import { useCropProtectionProducts } from '/@/hooks/catalog/use-crop-protection-products';
import { useMutateCreateCropMonitoringNote } from '/@/hooks/crop-monitoring-notes';
import { useNitrogenSources } from '/@/hooks/nitrogen-smart';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import type { RegistryAddStep } from './types';

const UNITS = ['lts/ha', 'cc/ha', 'kg/ha', 'gr/ha', 'ml/ha'];
const OTHER = 'other';
/** Not a real crop-protection product type — selects the nitrogen-source product list instead. */
const FERTILIZANTES = 'fertilizantes';
type TypeValue = number | typeof FERTILIZANTES | '';

interface TypeOption {
    id: number | typeof FERTILIZANTES;
    name: string;
}

/** Fixed display order for "Tipo de aplicación" — anything not listed here (incl. a literal
 *  "Otros" catalog type) sorts after Fertilizante, in whatever order it was fetched in. */
const TYPE_ORDER = ['herbicida', 'insecticida', 'fungicida', 'tratamiento de semilla'];
const normalizeTypeName = (name: string) =>
    name
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

interface ProductRow {
    productId: number | typeof OTHER | '';
    productName: string;
    dosis: string;
    unit: string;
    unitOther: string;
}

const emptyRow = (): ProductRow => ({ productId: '', productName: '', dosis: '', unit: '', unitOther: '' });

interface NitrogenSourceOption {
    id: number;
    name: string;
}

const rowIsValid = (row: ProductRow) =>
    (row.productId === OTHER ? row.productName.trim() !== '' : row.productId !== '') &&
    row.dosis.trim() !== '' &&
    (row.unit === _('Otro') ? row.unitOther.trim() !== '' : row.unit !== '');

const CreateRegistryApplication = ({
    open,
    onClose,
    onSuccess,
    setStep,
    fieldId,
    initialProductNames,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    setStep?: (step: RegistryAddStep) => void;
    fieldId?: number;
    /** Prefill for the "Registrar lo aplicado" flow — seeds product rows (and the type) from a
     *  recommendation's recommended products, matched to the catalog by name. */
    initialProductNames?: string[];
}) => {
    const { products, types } = useCropProtectionProducts();
    const { data: nitrogenSources } = useNitrogenSources() as { data: NitrogenSourceOption[] | undefined };
    const createApplication = useMutateCreateApplicationRegister();
    const createNote = useMutateCreateCropMonitoringNote();

    const [applicationDate, setApplicationDate] = useState<Dayjs | null>(null);
    const [typeId, setTypeId] = useState<TypeValue>('');
    const [note, setNote] = useState('');
    const [rows, setRows] = useState<ProductRow[]>([emptyRow()]);

    const isFertilizantes = typeId === FERTILIZANTES;

    // Seed product rows + type from the recommendation once the catalog is available. The
    // date, dose and unit are left for the user to complete ("con o sin cambios").
    const prefillApplied = useRef(false);
    useEffect(() => {
        if (!open) {
            prefillApplied.current = false;
            return;
        }
        if (prefillApplied.current || !initialProductNames?.length || !products.length) return;
        prefillApplied.current = true;

        const normalize = (name: string) => name.trim().toLowerCase();
        setRows(
            initialProductNames.map((name) => {
                const match = products.find((product) => normalize(product.name) === normalize(name));
                return match
                    ? { productId: match.id, productName: '', dosis: '', unit: '', unitOther: '' }
                    : { productId: OTHER, productName: name, dosis: '', unit: '', unitOther: '' };
            })
        );
        const firstMatch = products.find((product) =>
            initialProductNames.some((name) => normalize(product.name) === normalize(name))
        );
        if (firstMatch?.type?.id) setTypeId(firstMatch.type.id);
    }, [open, initialProductNames, products]);

    const typeProducts =
        typeId === '' || isFertilizantes ? products : products.filter((product) => product.type?.id === typeId);
    const nitrogenSourceOptions = nitrogenSources ?? [];

    // Herbicida, Insecticida, Fungicida, Tratamiento de semilla, Fertilizante, then anything
    // else (e.g. a catalog "Otros" type) in whatever order it was fetched in.
    const orderedTypeOptions = useMemo<TypeOption[]>(() => {
        const fertilizanteOption: TypeOption = { id: FERTILIZANTES, name: _('Fertilizante') };
        const rank = (name: string) => {
            const index = TYPE_ORDER.indexOf(normalizeTypeName(name));
            return index === -1 ? TYPE_ORDER.length + 1 : index;
        };
        return [...types, fertilizanteOption]
            .map((type, index) => ({ type, index }))
            .sort((a, b) => {
                const rankA = a.type.id === FERTILIZANTES ? TYPE_ORDER.length : rank(a.type.name);
                const rankB = b.type.id === FERTILIZANTES ? TYPE_ORDER.length : rank(b.type.name);
                return rankA !== rankB ? rankA - rankB : a.index - b.index;
            })
            .map(({ type }) => type);
    }, [types]);

    const updateRow = (index: number, patch: Partial<ProductRow>) =>
        setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

    const isValid =
        Boolean(applicationDate) && typeId !== '' && rows.length > 0 && rows.every(rowIsValid) && Boolean(fieldId);

    const handleSubmit = async () => {
        if (!isValid || !applicationDate || !fieldId) return;

        const applicationDateStr = applicationDate.format('YYYY-MM-DD');
        let firstId: number | undefined;

        for (const row of rows) {
            const isOther = row.productId === OTHER;
            const created = await createApplication.trigger(
                isFertilizantes
                    ? {
                          field: fieldId,
                          applicationDate: applicationDateStr,
                          type: null,
                          nitrogenSource: row.productId as number,
                          cropProtectionProduct: null,
                          cropProtectionProductName:
                              nitrogenSourceOptions.find((source) => source.id === row.productId)?.name ?? '',
                          dosis: Number(row.dosis),
                          unity: row.unit === _('Otro') ? row.unitOther.trim() : row.unit,
                      }
                    : {
                          field: fieldId,
                          applicationDate: applicationDateStr,
                          type: typeId as number,
                          cropProtectionProduct: isOther ? null : (row.productId as number),
                          cropProtectionProductName: isOther
                              ? row.productName.trim()
                              : (products.find((p) => p.id === row.productId)?.name ?? ''),
                          dosis: Number(row.dosis),
                          unity: row.unit === _('Otro') ? row.unitOther.trim() : row.unit,
                      }
            );
            if (firstId === undefined) firstId = created?.id;
        }

        if (note.trim() && firstId) {
            await createNote.trigger({ field: fieldId, applicationRegister: firstId, content: note.trim() });
        }

        onSuccess();
    };

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            goBack={setStep ? () => setStep('select') : undefined}
            title={_('Registrar una aplicación')}
            bodyProps={{ alignItems: 'stretch', textAlign: 'left', spacing: 3 }}
            actionsProps={{ justifyContent: 'flex-end' }}
            actions={
                <Button
                    variant='contained'
                    color='secondary'
                    disabled={!isValid || createApplication.isMutating}
                    onClick={handleSubmit}
                >
                    {_('Registrar en el seguimiento')}
                </Button>
            }
        >
            <Stack spacing={3}>
                <Stack spacing={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Fecha de aplicación')}
                    </Typography>
                    <MuiDatePicker
                        fullWidth
                        value={applicationDate}
                        onChange={(value) => setApplicationDate(value)}
                        slotProps={{ textField: { placeholder: _('Seleccionar una fecha') } }}
                        maxDate={dayjs()}
                    />
                </Stack>

                <Stack spacing={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Tipo de aplicación')}
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        value={typeId}
                        onChange={(event) => {
                            const value = event.target.value;
                            setTypeId(value === FERTILIZANTES ? FERTILIZANTES : Number(value));
                            // Product options depend on the type — clear any chosen products.
                            setRows([emptyRow()]);
                        }}
                        SelectProps={{ displayEmpty: true }}
                    >
                        <MenuItem value='' disabled>
                            {_('Elegir tipo')}
                        </MenuItem>
                        {orderedTypeOptions.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                {rows.map((row, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and have no stable id
                    <Stack key={index} spacing={2}>
                        <Typography variant='overline' color='primary'>
                            {`${_('Producto')} ${index + 1}`}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            value={row.productId}
                            disabled={typeId === ''}
                            onChange={(event) => {
                                const value = event.target.value;
                                updateRow(index, {
                                    productId: value === OTHER ? OTHER : Number(value),
                                    productName: '',
                                });
                            }}
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value='' disabled>
                                {isFertilizantes ? _('Elegir fuente de nitrógeno') : _('Elegir producto')}
                            </MenuItem>
                            {isFertilizantes
                                ? nitrogenSourceOptions.map((source) => (
                                      <MenuItem key={source.id} value={source.id}>
                                          {source.name}
                                      </MenuItem>
                                  ))
                                : typeProducts.map((product) => (
                                      <MenuItem key={product.id} value={product.id}>
                                          {product.name}
                                      </MenuItem>
                                  ))}
                            {!isFertilizantes && <MenuItem value={OTHER}>{_('Otro')}</MenuItem>}
                        </TextField>

                        {!isFertilizantes && row.productId === OTHER && (
                            <TextField
                                fullWidth
                                placeholder={_('Nombre del producto')}
                                value={row.productName}
                                onChange={(event) => updateRow(index, { productName: event.target.value })}
                            />
                        )}

                        <Stack direction='row' spacing={2}>
                            <TextField
                                fullWidth
                                type='number'
                                placeholder={_('Dosis')}
                                value={row.dosis}
                                onChange={(event) => updateRow(index, { dosis: event.target.value })}
                            />
                            <TextField
                                select
                                fullWidth
                                value={row.unit}
                                onChange={(event) => updateRow(index, { unit: event.target.value })}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value='' disabled>
                                    {_('Unidad')}
                                </MenuItem>
                                {UNITS.map((unit) => (
                                    <MenuItem key={unit} value={unit}>
                                        {unit}
                                    </MenuItem>
                                ))}
                                <MenuItem value={_('Otro')}>{_('Otro')}</MenuItem>
                            </TextField>
                        </Stack>

                        {row.unit === _('Otro') && (
                            <TextField
                                fullWidth
                                placeholder={_('Unidad')}
                                value={row.unitOther}
                                onChange={(event) => updateRow(index, { unitOther: event.target.value })}
                            />
                        )}

                        {index === rows.length - 1 && (
                            <>
                                <Divider />
                                <Button
                                    variant='text'
                                    color='primary'
                                    startIcon={<Add />}
                                    sx={{ alignSelf: 'flex-start' }}
                                    onClick={() => setRows((current) => [...current, emptyRow()])}
                                >
                                    {_('Agregar otro producto')}
                                </Button>
                            </>
                        )}
                    </Stack>
                ))}

                <Stack spacing={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Nota')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder={_('Escribir una observación')}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                    />
                </Stack>
            </Stack>
        </ModalV2>
    );
};

export default CreateRegistryApplication;
